import { MarketingParams } from "components/flexComponents/HotwireAffiliateToast/utils/marketingParams";
import { HotwireConfigStore } from "stores/HotwireConfigStore";
import { getStorageItem, setStorageItem } from "components/utility/hotwire/HotwireStorageUtil";
import { fetchHotwireApi, HotwireFetchMethod } from "components/utility/hotwire/HotwireApiUtil";
import { safeResponseToJson } from "components/utility/FetchUtils";
import {
  isBackNavigation,
  isNewTab,
  isRefreshedTab,
  isSameSession,
} from "components/flexComponents/HotwireAffiliateToast/utils/navigation";
import { FAILED_HOTW_CUSTOMER_STATS } from "config/systemEvents";

export interface CustomerStats {
  marketingChannel: MarketingChannel;
}

export enum MarketingChannel {
  DTI = "DTI",
  AFFILIATE = "AFF",
}

const STORAGE_KEY = "cststts.stts.v1.cst";
const STORAGE_MAX_AGE = 30 * 60 * 1000; // 30 minutes

const setCustomerStatsStorage = (value: CustomerStats) => setStorageItem(STORAGE_KEY, value, STORAGE_MAX_AGE);

const getCustomerStatsFromStorage = () => getStorageItem<CustomerStats>(STORAGE_KEY);

const getCustomerStatsFromApi = (
  marketingParams: MarketingParams,
  hotwireConfig: HotwireConfigStore
): Promise<CustomerStats> =>
  fetchHotwireApi(hotwireConfig.config.customerStatsService, {
    method: HotwireFetchMethod.GET,
    query: marketingParams,
  })
    .then(safeResponseToJson)
    .catch((error: Error) => {
      hotwireConfig.logger.logEvent(FAILED_HOTW_CUSTOMER_STATS, error);
    });

/**
 * Determines, based on the navigation state, whether to use the cached customer stats or to do an API call.
 * This is necessary to achieve consistency with non-Blossom Hotwire pages.
 */
const shouldUseStoredStats = () => !isNewTab() && (isSameSession() || isRefreshedTab() || isBackNavigation());

/**
 * Returns the customer stats, either from cache or API.
 */
export const getCustomerStats = (
  marketingParams: MarketingParams,
  hotwireConfig: HotwireConfigStore
): Promise<CustomerStats> => {
  const storedStats = getCustomerStatsFromStorage();

  if (storedStats !== null && shouldUseStoredStats()) {
    return Promise.resolve(storedStats);
  }

  return getCustomerStatsFromApi(marketingParams, hotwireConfig).then((stats) => {
    setCustomerStatsStorage(stats);

    return stats;
  });
};
